import { ArrowUpRightIcon } from '@c/icons';
import type { ChatDocument } from '@models/chat';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import { markAsRead } from '@util/firestore/messages';
import { getOrderById, getOrders } from '@util/firestore/orders';
import { getProductsByIds } from '@util/firestore/products';
import { isMobile, isTablet } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import Link from 'next/link';
import React, { useEffect } from 'react';
import ConversationList from './ConversationList';
import type { UseConversationsReturn } from 'context/ConversationsContext';
import { MessageCircleMoreIcon } from 'lucide-react';
import ChatWindow from '@c/chat/ChatWindow';
import { logError } from '@util/logError';

export const OrderPreview = ({
  chat,
  isSidebar,
}: {
  chat: ChatDocument;
  isSidebar?: boolean;
}) => {
  const { userDoc } = useAuth();
  const { data: order, isLoading: isLoadingOrder } = useQuery({
    queryKey: ['order', chat.id, userDoc?.uid],
    queryFn: async () => {
      const uid = userDoc?.uid;
      if (!uid) return null;
      if (userDoc.roles?.support) {
        const orders = await getOrders({
          id: chat.order_id,
        });
        return orders.length ? orders[0] : null;
      }
      try {
        if (uid === chat.buyer_id) {
          const orders = await getOrders({
            buyer_id: uid,
            id: chat.order_id,
          });
          return orders.length ? orders[0] : null;
        }
        if (uid === chat.seller_id) {
          const orders = await getOrders({
            seller_arr: [chat.seller_id],
            id: chat.order_id,
          });
          return orders.length ? orders[0] : null;
        }
      } catch (e) {
        // fallback cause there was an issue where the seller was getting set to the buyer id when support messaged the seller
        if (
          (e as Error).message.includes(
            'Missing or insufficient permissions'
          ) &&
          uid === chat.buyer_id &&
          chat.seller_id === process.env.NEXT_PUBLIC_SUPPORT_ID
        ) {
          logError(new Error(`FIX THIS CHAT: ${chat.id}`));
          const orders = await getOrders({
            seller_arr: [chat.buyer_id],
            id: chat.order_id,
          });
          return orders.length ? orders[0] : null;
        }
        throw e;
      }
    },
    enabled: !!chat.order_id,
  });

  const { setChatOpen } = useChat();

  const { data: products } = useQuery({
    queryKey: ['products', order?.product_ids],
    queryFn: () => getProductsByIds(order?.product_ids ?? []),
    enabled: !!order?.product_ids,
  });

  if (isLoadingOrder) {
    return (
      <div className="mx-auto my-4 h-[3rem] w-11/12 animate-pulse rounded-2xl bg-brand-gray" />
    );
  }
  if (!order) return null;
  const getHref = () => {
    if (
      userDoc &&
      (userDoc.uid === order.buyer_id || userDoc?.roles?.support)
    ) {
      return `/dashboard/purchases/${order.id}`;
    }
    return `/dashboard/orders/${order.id}`;
  };

  return (
    <>
      {isSidebar || isMobile() ? (
        <div className="lg flex items-center justify-between gap-[1.2rem] bg-brand-lightest-gray px-[2.1rem] py-[1.2rem]">
          <div className="flex flex-col gap-[0.8rem]">
            <Link href={getHref()} className="font-medium text-zinc-700">
              Order #{order.order_num}
            </Link>

            {order.product_ids && (
              <p className="text-[1.4rem] text-zinc-600">
                {order.product_ids.length} item(s)
              </p>
            )}

            {!!order.ra_fee && userDoc?.roles?.support && (
              <p className="text-[1.6rem] font-semibold">
                RA Fee: ${order.ra_fee}
              </p>
            )}
          </div>

          <Button
            type="text"
            leadingIcon={<ArrowUpRightIcon />}
            width="extraSmall"
            height="small"
            onClick={() => setChatOpen(false)}
            href={getHref()}
          />
        </div>
      ) : (
        <div className="hidden h-full min-w-[40rem] gap-[1.2rem] border-l border-zinc-200 bg-transparent bg-white px-[1.6rem] py-[1.2rem] sm:flex">
          <div className={`flex w-full flex-col gap-[1.6rem]`}>
            <Link href={getHref()} className="text-[2.4rem] font-medium ">
              Order #{order.order_num}
            </Link>

            <div className="flex flex-col gap-[1.6rem]">
              {products?.map((product) => {
                if (
                  chat.uids &&
                  !chat.uids.includes(product.seller_id) &&
                  !userDoc?.roles?.support
                )
                  return null;
                const item = Object.values(order.sellers ?? {})
                  .flat()
                  .find((i) => i.id === product.id);
                if (!item) return null;
                const image =
                  product.variations?.find(
                    (v) =>
                      (!item.variations?.letter ||
                        v.size === item.variations?.letter) &&
                      (!item.variations?.number ||
                        v.size === item.variations?.number) &&
                      (!item.variations?.color ||
                        v.color === item.variations.color)
                  )?.image ?? product.thumbnail;
                return (
                  <div key={product.id} className="flex gap-[1.6rem]">
                    <SafeImage
                      src={image}
                      key={image}
                      alt="product image"
                      className="aspect-square object-contain"
                      height={80}
                      width={80}
                    />
                    <div className="flex flex-col justify-center">
                      <span className="text-[1.8rem] font-semibold">
                        {product.title}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {!!order.ra_fee && userDoc?.roles?.support && (
              <p className="text-[1.6rem] font-semibold">
                RA Fee: ${order.ra_fee}
              </p>
            )}

            <Button
              leadingIcon={<ArrowUpRightIcon />}
              onClick={() => setChatOpen(false)}
              text="View Order"
              href={getHref()}
            />
          </div>
        </div>
      )}
    </>
  );
};

interface MessageCenterProps {
  chats: ChatDocument[];
  isShared?: boolean;
  isLoading?: boolean;
  isModal?: boolean;
}
export default function MessageCenter({
  chats,
  isLoading,
  isModal,
}: MessageCenterProps) {
  const { user } = useAuth();

  const [selectedChat, setSelectedChat] = React.useState<
    ChatDocument | undefined
  >();

  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    undefined
  );
  const [activeSearchQuery, setActiveSearchQuery] = React.useState<string>('');
  const queryClient = useQueryClient();

  const updateSelectedChat = (chat: ChatDocument, searchTermValue?: string) => {
    setSelectedChat(chat);
    setSearchTerm(searchTermValue);

    if (chat?.id && user?.uid && chat.unread?.[user.uid]) {
      markAsRead(chat.id, user.uid).then(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      });
    }
  };

  const isMobileDevice = isMobile() || isTablet();

  // selecting the first chat when the chats are loaded on desktop
  useEffect(() => {
    if (chats.length > 0 && !selectedChat && !isMobileDevice) {
      setSelectedChat(chats[0]);
    }
  }, [chats, selectedChat, isMobileDevice]);

  return (
    <div className="flex h-full w-full bg-[#ededed]">
      {/* Conversation List - hidden on mobile when chat is selected */}
      <div
        className={`${isMobileDevice && selectedChat ? 'hidden' : 'flex'} ${
          isMobileDevice ? 'w-full' : 'w-[55rem]'
        } flex-col`}
      >
        <ConversationList
          onChatSelected={updateSelectedChat}
          isLoading={isLoading}
          selectedChat={selectedChat}
          onSearchQueryChange={setActiveSearchQuery}
          propChats={chats}
          isModal={isModal}
        />
      </div>

      {/* Chat Window - full width on mobile, normal on desktop */}
      <div
        className={`${isMobileDevice && !selectedChat ? 'hidden' : 'flex'} ${
          isMobileDevice ? 'w-full' : 'flex-1'
        } flex-col`}
      >
        {!selectedChat ? (
          <div className="flex h-full w-full items-center justify-center">
            <div className="flex flex-col items-center gap-6">
              <MessageCircleMoreIcon className="h-24 w-24 text-zinc-300" />

              <span className="text-[1.8rem] text-zinc-400">
                Select a chat to view the conversation
              </span>
            </div>
          </div>
        ) : (
          <ChatWindow
            chat={selectedChat}
            searchTerm={activeSearchQuery || searchTerm}
            onChatArchived={() => setSelectedChat(undefined)}
            onGoBack={
              isMobileDevice ? () => setSelectedChat(undefined) : undefined
            }
          />
        )}
      </div>
    </div>
  );
}
